import { useMountEffect, type AsyncStatus } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "zustand";

import type { AssociatedPitchedContent } from "../../store/editContent";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { isSuccess } from "../../utils/api.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import { useClientSideLazyLoad } from "../misc/useClientSideLazyLoad";

import { usePitchedContentsAction } from "./usePitchedContentsAction";

export interface PitchedContentsLoaderProps {
    isLoaderInView?: boolean;
}

export function usePitchedContentsLoader({
    isLoaderInView,
}: PitchedContentsLoaderProps): [
    AsyncStatus,
    AssociatedPitchedContent[],
    number,
    () => void,
] {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const [contents, setContent] = useState<AssociatedPitchedContent[]>([]);

    const [contentsState, contentsAction] = usePitchedContentsAction();

    const fetchPitchedContents = useCallback(() => {
        void contentsAction.execute().then((pitchedContent) => {
            setContent(pitchedContent);
        });
    }, [contentsAction, setContent]);

    useMountEffect(() => {
        fetchPitchedContents();
    });

    const [lazyLoadedInsights] = useClientSideLazyLoad(
        contents,
        isLoaderInView ?? false,
    );

    useEffect(() => {
        if (isSuccess(contentsState.status)) {
            logInsightBrowseResults(contents.length, useUnifiedSearch);
        }
    }, [contents, contentsState.status]);

    return [
        contentsState.status,
        lazyLoadedInsights as AssociatedPitchedContent[],
        contents.length,
        fetchPitchedContents,
    ];
}
